import { ReactNode, Suspense, useMemo } from 'react'

import { useParams } from 'react-router-dom'

import { adminRoutes } from '@bettermode/domain/routes/web/admin'
import { SpinnerIcon } from '@bettermode/icons/solid/Spinner'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useRouter } from '@tribeplatform/react-sdk'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { SectionHeader } from '@tribeplatform/react-ui-kit/SectionHeader'
import { Sidebar } from '@tribeplatform/react-ui-kit/Sidebar'

import { SidebarBackButton } from '../../AdminMenu/SidebarBackButton.js'
import { SpaceBlocksSidebarTitle } from '../../AdminMenu/SpaceBlocksSidebarTitle.js'
import { useCustomizerSpace } from '../../Editor/hooks/index.js'
import { useCustomizer } from '../../Editor/hooks/use-customizer.hook.js'
import { SpaceSidebarEditor } from '../../Editor/Sidebar/index.js'
import { CustomizerActionType } from '../../Editor/types.js'
import { AnchorComponentProps } from '../../TopNavBar/modes/index.js'

export function CustomizerSpaceRoute() {
  const { $t } = useI18n()
  const { state: { activeBlock, blockSettings } = {}, dispatch } =
    useCustomizer()

  const header = useMemo(() => {
    if (!activeBlock) {
      return (
        <>
          <SidebarBackButton href={adminRoutes.design.customizer.root} />
          <SectionHeader title={<SpaceBlocksSidebarTitle />} />
        </>
      )
    }
    let title: ReactNode | string
    let description: ReactNode | string

    if (activeBlock.action === 'edit') {
      title = blockSettings.at(-1)?.title
      description = blockSettings.at(-1)?.description
    } else {
      title = $t({
        defaultMessage: 'Add block',
        id: 'Admin.Customizer.AddBlock',
      })
    }

    const popSettings = blockSettings.length > 1

    return (
      <div className="space-y-4 pb-4">
        <SidebarBackButton
          onClick={e => {
            if (activeBlock) {
              e.preventDefault()
              e.stopPropagation()

              if (popSettings) {
                dispatch({
                  type: CustomizerActionType.POP_BLOCK_SETTINGS,
                })
              } else {
                dispatch({
                  type: CustomizerActionType.DEACTIVATE_BLOCK,
                })
              }
            }
          }}
        />
        <SectionHeader title={title} description={description} />
      </div>
    )
  }, [activeBlock, blockSettings, dispatch, $t])

  return (
    <Sidebar size="lg">
      <Sidebar.Content className="px-3 space-y-4">
        {header}
        <Suspense fallback={<></>}>
          <SpaceSidebarEditor />
        </Suspense>
      </Sidebar.Content>
    </Sidebar>
  )
}

const colon = ':'

export function CustomizerSpaceBreadCrumb() {
  const { Link } = useRouter()
  const { space } = useCustomizerSpace()
  return (
    <Link
      href={adminRoutes.design.customizer.spaceBlocks(space?.id)}
      className="flex items-center gap-1"
    >
      <SvgIcon name="folder" />
      <span className="text-sm">
        <T id="Generics.Space" defaultMessage="{SPACE_CC}" />
        {colon}
      </span>
      {space?.name ? space.name : <SpinnerIcon className="animate-spin" />}
    </Link>
  )
}

export function CustomizerSpaceAnchorToAdmin(props: AnchorComponentProps) {
  const { id: spaceId } = useParams<{
    id: string
  }>()
  const { Link } = useRouter()

  return <Link href={adminRoutes.site.spaces.general(spaceId)} {...props} />
}

export function CustomizerSpaceAnchorToModeration(props: AnchorComponentProps) {
  const { id: spaceId } = useParams<{
    id: string
  }>()
  const { Link } = useRouter()

  return <Link href={`/settings/moderation/${spaceId}`} {...props} />
}

export function CustomizerSpaceAnchorToVisitSite(props: AnchorComponentProps) {
  const { space } = useCustomizerSpace()
  const { Link } = useRouter()
  const href = space?.relativeUrl || '/'
  return <Link href={href} target="_blank" {...props} />
}
